//SegmentEditingButtons.vue with v-dialog
<template>
  <div class="segment-editing-buttons" :class="{ 'is-visible': isVisible }">
    <!-- Delete button -->
    <button
      class="segment-btn delete-btn"
      title="Delete this segment"
      @click="handleDelete"
      :disabled="isDeleting"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 6h18"></path>
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
      </svg>
      <span v-if="isDeleting" class="action-text">Deleting...</span>
    </button>

    <!-- Add before button -->
    <button
      class="segment-btn add-btn add-before-btn"
      title="Add segment before this one"
      @click="showAddDialog('before')"
      :disabled="isAdding"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 5v14"></path>
        <path d="M5 12h14"></path>
      </svg>
      <span class="position-indicator">Before</span>
    </button>

    <!-- Add after button -->
    <button
      class="segment-btn add-btn add-after-btn"
      title="Add segment after this one"
      @click="showAddDialog('after')"
      :disabled="isAdding"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 5v14"></path>
        <path d="M5 12h14"></path>
      </svg>
      <span class="position-indicator">After</span>
    </button>

    <!-- Add segment dialog using v-dialog -->
    <v-dialog
      v-model="showDialog"
      max-width="600px"
      persistent
      content-class="segment-dialog"
    >
      <v-card>
        <v-card-title class="dialog-title">
          Add New Segment {{ insertPosition === 'before' ? 'Before' : 'After' }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newSegment.speaker"
                  label="Speaker"
                  placeholder="Speaker name"
                  outlined
                  dense
                  ref="speakerInput"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newSegment.role"
                  label="Role"
                  placeholder="Speaker role"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="newSegment.content"
                  label="Content"
                  placeholder="Enter segment content"
                  outlined
                  auto-grow
                  rows="5"
                  counter="500"
                  :rules="[v => v.length <= 500 || 'Maximum 500 characters']"
                  ref="contentInput"
                  @keydown.ctrl.enter="addSegment"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey darken-1"
            @click="closeDialog"
            :disabled="isSubmitting"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="addSegment"
            :disabled="!newSegment.content || isSubmitting || newSegment.content.length > 500"
            :loading="isSubmitting"
          >
            Add Segment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import API from '@/api';

export default {
  name: 'SegmentEditingButtons',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    messageId: {
      type: Number,
      required: true
    },
    segmentIndex: {
      type: Number,
      required: true
    },
    currentSpeaker: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isDeleting: false,
      isAdding: false,
      isSubmitting: false,
      showDialog: false,
      insertPosition: 'before',
      newSegment: {
        speaker: '',
        role: '',
        content: ''
      }
    };
  },
  computed: {
    contentChars() {
      return this.newSegment.content ? this.newSegment.content.length : 0;
    }
  },
  methods: {
    async handleDelete() {
      if (confirm('Are you sure you want to delete this segment? This cannot be undone.')) {
        this.isDeleting = true;
        try {
          const response = await API.delete(`/user_customization/messages/${this.messageId}/segments/${this.segmentIndex}`);

          if (response.data.success) {
            // Pass the updated response to the parent
            this.$emit('segment-deleted', this.segmentIndex, response.data.new_segment_count, response.data.updated_response);
          }
        } catch (error) {
          console.error('Error deleting segment:', error);
          this.$toast?.error('Failed to delete segment. Please try again.') ||
            alert('Failed to delete segment. Please try again.');
        } finally {
          this.isDeleting = false;
        }
      }
    },

    showAddDialog(position) {
      this.insertPosition = position;

      // Pre-fill with current speaker and role
      this.newSegment = {
        speaker: this.currentSpeaker.name || '',
        role: this.currentSpeaker.role || '',
        content: ''
      };

      this.showDialog = true;

      // Focus the content input after dialog appears
      this.$nextTick(() => {
        if (this.$refs.contentInput) {
          this.$refs.contentInput.focus();
        }
      });
    },

    closeDialog() {
      if (!this.isSubmitting) {
        this.showDialog = false;
      }
    },

    async addSegment() {
      if (!this.newSegment.content || this.isSubmitting || this.newSegment.content.length > 500) {
        return;
      }

      this.isSubmitting = true;

      try {
        const payload = {
          position: this.insertPosition,
          content: this.newSegment.content,
          speaker: this.newSegment.speaker || 'Speaker',
          role: this.newSegment.role || 'Role'
        };

        const response = await API.post(
          `/user_customization/messages/${this.messageId}/segments/${this.segmentIndex}/insert`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Pass the updated response to the parent
          this.$emit('segment-added', {
            position: this.insertPosition,
            index: this.segmentIndex,
            insertedAt: response.data.insert_position,
            segment: response.data.new_segment,
            newSegmentCount: response.data.new_segment_count,
            updatedResponse: response.data.updated_response
          });

          this.closeDialog();
        }
      } catch (error) {
        console.error('Error adding segment:', error);
        this.$toast?.error('Failed to add segment. Please try again.') ||
          alert('Failed to add segment. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.segment-editing-buttons {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: opacity 0.2s, transform 0.2s;
}

.segment-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  transition: background-color 0.2s, color 0.2s;
}

.segment-btn:hover {
  background-color: #f0f0f0;
  color: #333;
}

.segment-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.segment-btn svg {
  margin-right: 4px;
}

.delete-btn {
  color: #e53935;
}

.delete-btn:hover {
  background-color: rgba(229, 57, 53, 0.1);
}

.add-btn {
  color: #2196f3;
}

.add-btn:hover {
  background-color: rgba(33, 150, 243, 0.1);
}

.position-indicator {
  font-size: 12px;
  margin-left: 2px;
}

/* Custom dialog styling */
.segment-dialog {
  z-index: 1000 !important;
}

.dialog-title {
  position: relative;
  padding-bottom: 16px;
  font-size: 18px;
  font-weight: 500;
}

.close-btn {
  position: absolute;
  top: 0;
  right: 0;
}

/* Override Vuetify styles if needed */
:deep(.v-text-field__details),
:deep(.v-messages) {
  min-height: 18px !important;
}

:deep(.v-input__slot) {
  margin-bottom: 0 !important;
}
</style>
